import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"
import { ParkingSize, storedParkingSizeField } from "./parkingSpot"

export enum EngineType {
    Gasoline = 0,
    Electric,
    NaturalGas,
    Diesel,
    Plugin,
    Hydrogen
}
export const allEngineTypes: EngineType[] = Object.values(EngineType).filter(value => typeof value === "number")

export const getEngineTypeIcon = (engineType?: EngineType): string => {
    if (engineType === undefined) return ""
    switch (engineType) {
        case EngineType.Gasoline:
            return "🟢"
        case EngineType.Electric:
            return "⚡️"
        case EngineType.NaturalGas:
            return "🔵"
        case EngineType.Diesel:
            return "⚫️"
        case EngineType.Plugin:
            return "🔌⚡️"
        case EngineType.Hydrogen:
            return "💧"
        default:
            return ""
    }
}

const carSchema = z.object({
    plateNum: z.string(),
    timestampCreated: z.custom<Timestamp>().nullish(),
    brand: z.string().nullish(),
    model: z.string().nullish(),
    color: z.string().nullish(),
    deleted: z.boolean().default(false),
    engineType: z.enum(EngineType).default(EngineType.Gasoline),
    size: storedParkingSizeField
})

interface ICar {
    id: string
    plateNum: string
    timestampCreated?: Date
    brand?: string
    model?: string
    color?: string
    deleted: boolean
    engineType: EngineType
    /** Size class of the car, set by the user. Optional on the model; unset is treated as Regular by the matcher. */
    size?: ParkingSize
}

export interface Car extends ICar {}
export class Car {
    get isElectric(): boolean {
        return this.engineType === EngineType.Electric || this.engineType === EngineType.Plugin
    }

    constructor(params: ICar) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): Car | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create Car ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = carSchema.parse(raw)
            return new Car({
                id: doc.id,
                plateNum: parsed.plateNum,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                brand: parsed.brand ?? undefined,
                model: parsed.model ?? undefined,
                color: parsed.color ?? undefined,
                deleted: parsed.deleted ?? false,
                engineType: parsed.engineType,
                size: parsed.size
            })
        } catch (error) {
            logger.error(`Cannot create Car ${doc.id} from snapshot:`, error)
            return null
        }
    }

    /**
     * The canonical stored form of a plate: whitespace-stripped and upper-cased, e.g. "1AB 2345" -> "1AB2345".
     * Null-safe - a missing plate normalises to "", so callers never have to pre-check. Uses the
     * locale-independent String.toUpperCase on purpose; a locale-sensitive mapping would make the stored
     * form depend on the runtime's locale and silently break matching.
     */
    static normalizePlate(plate: string | null | undefined): string {
        return (plate ?? "").replace(/\s+/g, "").toUpperCase()
    }

    /**
     * Firestore has no case- or whitespace-insensitive query, so to match a plate we query its possible
     * stored spellings. New writes are normalised (see asData), but historical documents still hold spaces,
     * so the space variants stay necessary. Returns at most 30 values (the Firestore "in" limit).
     *
     * Returns an EMPTY array for a missing or blank plate. Callers MUST treat that as "cannot match
     * anything" and skip the query entirely: Firestore rejects an empty "in" array, and querying for ""
     * would match every car with a blank plate and hand back an arbitrary unrelated user.
     */
    static plateMatchCandidates(plate: string | null | undefined): string[] {
        const compact = Car.normalizePlate(plate)
        if (compact === "") {
            return []
        }

        const raw = plate ?? ""
        const candidates = new Set<string>([raw, raw.trim(), compact])
        for (let i = 1; i < compact.length; i++) {
            candidates.add(`${compact.slice(0, i)} ${compact.slice(i)}`)
        }
        return Array.from(candidates).slice(0, 30)
    }

    asData(): { [k: string]: any } {
        return carSchema.parse({
            // Normalise only an actual string - anything else is left for the schema to reject loudly,
            // rather than being silently coerced into an empty plate.
            plateNum: typeof this.plateNum === "string" ? Car.normalizePlate(this.plateNum) : this.plateNum,
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            brand: this.brand,
            model: this.model,
            color: this.color,
            deleted: this.deleted ?? false,
            engineType: this.engineType,
            size: this.size
        })
    }

    toString(): string {
        const name = [this.brand, this.model].filter(Boolean).join(" ").trim()
        return `${name ? name + " " : ""}(${this.plateNum})`
    }

    public toShortString = (includeEngineType: boolean = true): string => {
        if (includeEngineType) {
            return this.plateNum + " " + getEngineTypeIcon(this.engineType)
        } else {
            return this.plateNum
        }
    }
}
